import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  MessageCircle, Heart, Sparkles, Clock, ChevronRight, Star, 
  Users, Calendar, Play, Check, Flame, Zap, Volume2, Globe, Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Animated typing effect for chat preview
const TypingEffect = ({ text, delay = 0 }) => {
  const [displayText, setDisplayText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      let i = 0;
      const interval = setInterval(() => {
        if (i <= text.length) {
          setDisplayText(text.slice(0, i));
          i++;
        } else {
          clearInterval(interval);
          setShowCursor(false);
        }
      }, 30);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timer);
  }, [text, delay]);
  
  return (
    <span>
      {displayText}
      {showCursor && <span className="animate-pulse">|</span>}
    </span>
  );
};

// Floating animation wrapper
const FloatingElement = ({ children, delay = 0, duration = 3 }) => (
  <motion.div
    animate={{ y: [0, -8, 0] }}
    transition={{ duration, repeat: Infinity, delay, ease: "easeInOut" }}
  >
    {children}
  </motion.div>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  // Auto-rotate how it works steps
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep(prev => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-dark-bg overflow-hidden">
      
      {/* Hero Section */}
      <section className="container mx-auto px-6 md:px-12 lg:px-16 pt-8 pb-16 md:pt-12 md:pb-24 relative">
        {/* Decorative gradient elements */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-vocalira/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-20 left-10 w-48 h-48 bg-cyan/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-vocalira/10 to-cyan/10 rounded-full blur-3xl pointer-events-none" />
        
        {/* Logo/Brand */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 mb-12"
        >
          <motion.div 
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            className="flex items-center gap-2"
          >
            <img 
              src="/logo.svg" 
              alt="Vocalira" 
              className="h-10 w-auto"
            />
          </motion.div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Tagline with pulse */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center gap-2 bg-vocalira/20 text-vocalira-light px-4 py-2 rounded-full text-sm font-medium border border-vocalira/30"
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Sparkles className="w-4 h-4" />
              </motion.div>
              Seu companheiro diário de conversação
            </motion.div>

            {/* Main Headline - Clear value prop */}
            <h1 className="font-fraunces text-4xl sm:text-5xl lg:text-6xl font-semibold text-dark-text leading-[1.1] tracking-tight">
              Pratique idiomas
              <br />
              <motion.span 
                className="text-transparent bg-clip-text bg-gradient-to-r from-vocalira via-vocalira-dark to-cyan inline-block"
                animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                transition={{ duration: 5, repeat: Infinity }}
                style={{ backgroundSize: '200% 200%' }}
              >
                conversando
              </motion.span>
              <br />
              todos os dias.
            </h1>

            {/* Sub-headline - Benefit focused */}
            <p className="text-lg text-dark-muted max-w-lg leading-relaxed">
              Conversas leves e naturais com a <strong className="text-vocalira">Lira</strong>, sua tutora de IA. 
              Sem exercícios chatos. Sem pressão. Evolua no seu ritmo.
            </p>

            {/* Quick benefits */}
            <div className="flex flex-wrap gap-4">
              {[
                { icon: Clock, text: '3 min por dia' },
                { icon: Flame, text: 'Streak diário' },
                { icon: Globe, text: '5 idiomas' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-2 text-sm text-dark-muted bg-dark-card px-3 py-1.5 rounded-full border border-dark-border"
                >
                  <item.icon className="w-4 h-4 text-vocalira" />
                  {item.text}
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <motion.div
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  data-testid="get-started-btn"
                  onClick={() => navigate('/register')}
                  className="w-full sm:w-auto bg-vocalira-gradient text-white hover:opacity-90 rounded-full px-8 py-7 text-lg font-semibold shadow-glow hover:shadow-glow transition-all group"
                >
                  Começar agora — é grátis
                  <motion.div
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </motion.div>
                </Button>
              </motion.div>
              <Button
                data-testid="login-btn"
                onClick={() => navigate('/login')}
                variant="ghost"
                className="text-sand hover:bg-dark-card rounded-full px-8 py-7 text-lg font-medium border border-dark-border"
              >
                Já tenho conta
              </Button>
            </div>

            {/* Trust signals */}
            <div className="flex items-center gap-4 text-sm text-dark-muted pt-2">
              <div className="flex -space-x-2">
                {[
                  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=40&h=40&fit=crop&crop=face',
                  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face',
                  'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face',
                  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
                ].map((src, i) => (
                  <motion.img 
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    src={src} 
                    alt="" 
                    className="w-8 h-8 rounded-full border-2 border-dark-card object-cover"
                  />
                ))}
              </div>
              <span>Milhares praticando diariamente</span>
            </div>
          </motion.div>

          {/* Right: Interactive Chat Preview */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <FloatingElement delay={0} duration={4}>
              <div className="bg-dark-card rounded-3xl shadow-dark-medium overflow-hidden border border-dark-border">
                {/* Phone header */}
                <div className="bg-dark-elevated px-6 py-4 flex items-center gap-3 border-b border-dark-border">
                  <motion.img 
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    src="https://images.unsplash.com/photo-1697063882499-f7fca7d2d713?w=60&h=60&fit=crop&crop=face"
                    alt="Lira"
                    className="w-10 h-10 rounded-full object-cover border-2 border-vocalira"
                  />
                  <div>
                    <p className="text-dark-text font-medium text-sm">Lira</p>
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-cyan rounded-full animate-pulse" />
                      <span className="text-cyan/80 text-xs">Online</span>
                    </div>
                  </div>
                </div>
                
                {/* Chat messages */}
                <div className="p-6 space-y-4 bg-dark-bg min-h-[280px]">
                  {/* Lira message */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex gap-3"
                  >
                    <div className="bg-dark-elevated rounded-2xl rounded-tl-sm px-4 py-3 border border-dark-border max-w-[85%]">
                      <p className="text-dark-text text-sm">
                        <TypingEffect text="Hi! Ready to practice English today? 😊 What's something fun you did recently?" delay={800} />
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <button className="p-1.5 bg-vocalira/20 rounded-full hover:bg-vocalira/30 transition-colors">
                          <Volume2 className="w-3 h-3 text-vocalira" />
                        </button>
                        <span className="text-xs text-dark-muted">Ouvir</span>
                      </div>
                    </div>
                  </motion.div>
                  
                  {/* User message */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 2.5 }}
                    className="flex justify-end"
                  >
                    <div className="bg-vocalira-gradient text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-[85%]">
                      <p className="text-sm">I went to the beach!</p>
                    </div>
                  </motion.div>
                  
                  {/* Lira response with correction */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 3.5 }}
                    className="flex gap-3"
                  >
                    <div className="bg-dark-elevated rounded-2xl rounded-tl-sm px-4 py-3 border border-dark-border max-w-[85%]">
                      <p className="text-dark-text text-sm">
                        That sounds lovely! 🏖️ How was the weather? Was it sunny or cloudy?
                      </p>
                      <p className="text-xs text-dark-muted mt-2 italic border-t border-dark-border pt-2">
                        Parece ótimo! Como estava o tempo?
                      </p>
                    </div>
                  </motion.div>
                </div>
                
                {/* Input area */}
                <div className="px-4 py-3 border-t border-dark-border flex items-center gap-2 bg-dark-card">
                  <div className="flex-1 bg-dark-elevated rounded-full px-4 py-2.5 text-sm text-dark-muted border border-dark-border">
                    Sua resposta...
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-10 h-10 bg-vocalira-gradient rounded-full flex items-center justify-center shadow-glow"
                  >
                    <Play className="w-4 h-4 text-white fill-white" />
                  </motion.button>
                </div>
              </div>
            </FloatingElement>
            
            {/* Floating badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="absolute -left-4 top-1/4 bg-dark-card px-4 py-2 rounded-xl shadow-dark-soft border border-dark-border"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <Flame className="w-4 h-4 text-orange-400" />
                </div>
                <div>
                  <p className="text-xs text-dark-muted">Streak</p>
                  <p className="text-sm font-bold text-dark-text">7 dias 🔥</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="absolute -right-4 bottom-1/4 bg-dark-card px-4 py-2 rounded-xl shadow-dark-soft border border-dark-border"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-cyan/20 rounded-lg flex items-center justify-center">
                  <Zap className="w-4 h-4 text-cyan" />
                </div>
                <div>
                  <p className="text-xs text-dark-muted">Hoje</p>
                  <p className="text-sm font-bold text-dark-text">+50 XP</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How It Works - Interactive */}
      <section className="bg-dark-card py-16 md:py-24 border-y border-dark-border">
        <div className="container mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-fraunces text-3xl md:text-4xl font-semibold text-dark-text mb-4">
              Simples como conversar
            </h2>
            <p className="text-lg text-dark-muted max-w-2xl mx-auto">
              Sem gramática complicada. Apenas conversas que te preparam para o mundo real.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                step: '1',
                title: 'Escolha um tema',
                description: 'Viagem, trabalho, restaurante... pratique o que você vai usar.',
                emoji: '🎯',
                color: 'from-vocalira/20 to-vocalira/5',
              },
              {
                step: '2',
                title: 'Converse com a Lira',
                description: 'Fale ou digite. Ela responde como uma amiga paciente.',
                emoji: '💬',
                color: 'from-cyan/20 to-cyan/5',
              },
              {
                step: '3',
                title: 'Evolua todo dia',
                description: 'Pequenas práticas, grandes resultados. Veja seu progresso.',
                emoji: '🌱',
                color: 'from-purple-500/20 to-purple-500/5',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className={`text-center p-8 rounded-3xl bg-gradient-to-br ${item.color} border border-dark-border cursor-pointer transition-all ${
                  activeStep === index ? 'ring-2 ring-vocalira shadow-glow' : ''
                }`}
                onClick={() => setActiveStep(index)}
              >
                <motion.div 
                  animate={activeStep === index ? { scale: [1, 1.2, 1] } : {}}
                  transition={{ duration: 0.5 }}
                  className="text-5xl mb-6"
                >
                  {item.emoji}
                </motion.div>
                <div className="inline-flex items-center justify-center w-8 h-8 bg-vocalira-gradient text-white text-sm font-bold rounded-full mb-4 shadow-glow">
                  {item.step}
                </div>
                <h3 className="font-fraunces text-xl font-semibold text-dark-text mb-3">
                  {item.title}
                </h3>
                <p className="text-dark-muted leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features/Benefits Grid */}
      <section className="py-16 md:py-24 bg-dark-bg">
        <div className="container mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-fraunces text-3xl md:text-4xl font-semibold text-dark-text mb-4">
              Tudo que você precisa para evoluir
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: MessageCircle, title: 'Conversas naturais', desc: 'Pratique como na vida real, sem scripts decorados', color: 'bg-vocalira/20 text-vocalira' },
              { icon: Volume2, title: 'Fale e ouça', desc: 'Reconhecimento de voz e pronúncia com IA', color: 'bg-purple-500/20 text-purple-400' },
              { icon: Heart, title: 'Sem julgamento', desc: 'Erre à vontade, a Lira te corrige com carinho', color: 'bg-pink-500/20 text-pink-400' },
              { icon: Flame, title: 'Streak diário', desc: 'Mantenha a sequência e veja seu progresso', color: 'bg-orange-500/20 text-orange-400' },
              { icon: Target, title: 'Missões diárias', desc: 'Pequenos desafios que te motivam a continuar', color: 'bg-cyan/20 text-cyan' },
              { icon: Globe, title: '5 idiomas', desc: 'Inglês, Espanhol, Francês, Alemão e Chinês', color: 'bg-teal-500/20 text-teal-400' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -3 }}
                className="bg-dark-card rounded-2xl p-6 border border-dark-border hover:border-vocalira/30 transition-all"
              >
                <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center mb-4`}>
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-dark-text mb-2">{item.title}</h3>
                <p className="text-dark-muted text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="bg-dark-elevated py-16 md:py-24 relative overflow-hidden border-y border-dark-border">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-32 h-32 bg-vocalira rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-cyan rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-6 md:px-12 lg:px-16 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="flex justify-center gap-1 mb-6">
              {[1,2,3,4,5].map((i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Star className="w-6 h-6 text-vocalira fill-vocalira" />
                </motion.div>
              ))}
            </div>
            
            <blockquote className="font-fraunces text-2xl md:text-3xl text-dark-text/90 mb-8 leading-relaxed">
              &ldquo;Pela primeira vez eu consigo praticar inglês sem vergonha. A Lira é como uma amiga paciente que me ajuda a melhorar.&rdquo;
            </blockquote>
            
            <div className="flex items-center justify-center gap-3">
              <img 
                src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=60&h=60&fit=crop&crop=face"
                alt="Ana"
                className="w-12 h-12 rounded-full object-cover border-2 border-vocalira/50"
              />
              <div className="text-left">
                <p className="text-dark-text font-medium">Ana Carolina</p>
                <p className="text-dark-muted text-sm">Praticando há 3 meses</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-dark-bg">
        <div className="container mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-vocalira/30 via-purple-500/20 to-cyan/20 rounded-3xl p-12 md:p-16 text-center relative overflow-hidden border border-dark-border"
          >
            <div className="absolute top-4 right-4 opacity-20">
              <MessageCircle className="w-24 h-24 text-vocalira" />
            </div>
            
            <div className="max-w-2xl mx-auto relative">
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-5xl mb-6"
              >
                💬
              </motion.div>
              <h2 className="font-fraunces text-3xl md:text-4xl font-semibold text-dark-text mb-4">
                Pronto para começar a conversar?
              </h2>
              <p className="text-lg text-dark-muted mb-8">
                Sua primeira conversa com a Lira está a um clique de distância.
              </p>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  data-testid="cta-get-started-btn"
                  onClick={() => navigate('/register')}
                  className="bg-vocalira-gradient text-white hover:opacity-90 rounded-full px-10 py-7 text-lg font-semibold shadow-glow hover:shadow-glow transition-all"
                >
                  Começar minha primeira conversa
                </Button>
              </motion.div>
              <p className="text-sm text-dark-muted mt-6 flex items-center justify-center gap-2">
                <Check className="w-4 h-4 text-cyan" />
                Grátis para sempre no plano básico
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-dark-border py-8 bg-dark-card">
        <div className="container mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="/logo.svg" 
                alt="Vocalira" 
                className="h-8 w-auto"
              />
            </div>
            
            <p className="text-dark-muted text-sm text-center">
              © 2026 Vocalira. Feito com 💜 para quem quer falar com o mundo.
            </p>
            
            <div className="flex gap-6">
              <a href="/terms" className="text-dark-muted hover:text-vocalira text-sm transition-colors">Termos</a>
              <a href="/privacy" className="text-dark-muted hover:text-vocalira text-sm transition-colors">Privacidade</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
